import { computed, reactive } from 'vue'
import type { ComputedRef } from 'vue'
import { ErrorCode, PROMPTYPE, SORTTYPE, XHR_CONTENT_TYPE, XHR_REQUEST_TYPE } from '@/constants.js'
import useApiService from '@/apiRequest.js'
import { useCMSStore } from './CMSStore.js'
import useDialogStore from '@/composition/stores/dialogStore.js'
import type { APIRequestPayload } from '@/models/main.js'
import {
  type IPieData,
  PromptSchema,
  Contribution,
  DocumentMetadata,
  type IPath,
  Message
} from '@/models/legal.js'
import type { IMessageAggregate, IMessageResponse, IDocumentFeedback } from '@teams'

interface IMsgAgg {
  mentionsCount: number
  reactionsCount: number
  attachmentsCount: number
  messageCount: number
}
interface AIResponse {
  transcript: string
  resultFromPrompt: string
}

interface BarChart {
  date: number
  [key: string]: number
}

interface State {
  selectedTeam: string // id
  isTesting: boolean
  contributions: IPieData[]
  teamMembers: Record<string, string[]>
  transcribedText: string | undefined
  allMessages: Message | undefined

  messageAgg: IMsgAgg | undefined

  barPostMap: BarChart[]
  taskFeedback: IDocumentFeedback[]
  meetingFeedback: IDocumentFeedback[]
  documentMetaDataAnalytics: DocumentMetadata
  version: IPieData[]
}
const state: State = reactive({
  selectedTeam: '',
  isTesting: false,
  contributions: [],
  teamMembers: {},
  transcribedText: '',
  allMessages: undefined,

  messageAgg: undefined,

  barPostMap: [],
  taskFeedback: [],
  meetingFeedback: [],
  documentMetaDataAnalytics: {
    totalModifications: 0,
    totalUploads: 0,
    aggVersionbyUser: {},
    aggUploadbyUser: {},
    versionInfo: []
  },
  version: []
})

// ------------  Internal functions ------------

const { getters: CMSGetters } = useCMSStore()
const apiService = useApiService()
const dialogStore = useDialogStore()

const teamOnReload = sessionStorage.getItem('team')
if (teamOnReload) state.selectedTeam = teamOnReload

// Trial to see how Open ai works
async function fetchOpenAi(audio: FormData): Promise<AIResponse> {
  const payload: APIRequestPayload = {
    errorCode: ErrorCode.TEAMS,
    method: XHR_REQUEST_TYPE.POST,
    credentials: true,
    route: '/api/teams/resultfromAI',
    body: audio,
    contentType: XHR_CONTENT_TYPE.MULTIPART
  }
  const response = await apiService.apiRequest<AIResponse>(payload)
  if (response.error) {
    response.error.message = 'Unable to fetch from OpenAI'
    dialogStore.actions.handleError(response)
  }
  return (
    response.data ?? {
      transcript: '',
      resultFromPrompt: ''
    }
  )
}

/**
 * Upload file for meeting or Document analytics
 * @param file Actuall file
 * @param path path needs team_id and type of construct
 * @returns if uploaded correctly
 */
async function postUpload(file: FormData, path: IPath): Promise<void> {
  const payload: APIRequestPayload = {
    errorCode: ErrorCode.TEAMS,
    method: XHR_REQUEST_TYPE.POST,
    credentials: true,
    route: '/api/teams/upload',
    body: file,
    query: {
      team: path.selectedTeam,
      mode: path.selectedMode
    },
    contentType: XHR_CONTENT_TYPE.MULTIPART
  }
  const response = await apiService.apiRequest<void>(payload)
  if (response.error) {
    response.error.message = 'Unable to post upload'
    dialogStore.actions.handleError(response)
  }
  return response.data
}

// Document feedback
async function fetch_document_feedback(documentPrompt: PromptSchema): Promise<IDocumentFeedback[]> {
  const body = {
    prompt: documentPrompt.prompt,
    systemPrompt: documentPrompt.instruction,
    promptSection: documentPrompt.promptSection,
    promptType: documentPrompt.promptType,
    team_id: state.selectedTeam || ''
  }
  const payload: APIRequestPayload = {
    errorCode: ErrorCode.TEAMS,
    method: XHR_REQUEST_TYPE.POST,
    credentials: true,
    route: '/api/teams/taskFeedback',
    body,
    contentType: 'application/json'
  }
  const response = await apiService.apiRequest<IDocumentFeedback[]>(payload)
  if (response.error) {
    response.error.message = 'Unable to get doocument feedback'
    dialogStore.actions.handleError(response)
  }
  return response.data ?? []
}

// To list all messages for both Teams and chats for a selected group
async function fetchMessages(
  options: { testing: boolean } = { testing: false }
): Promise<IMessageResponse> {
  const payload: APIRequestPayload = {
    errorCode: ErrorCode.TEAMS,
    method: XHR_REQUEST_TYPE.GET,
    credentials: true,
    route: '/api/teams/messages',
    query: {
      team_id: state.selectedTeam || '',
      testing: options.testing ? 'true' : 'false'
    }
  }
  const response = await apiService.apiRequest<IMessageResponse>(payload)
  if (response.error) {
    response.error.message = 'Unable to get messages'
    dialogStore.actions.handleError(response)
  }
  return (
    response.data ?? {
      messageAggregate: [],
      postCount: []
    }
  )
}

// To fetch all Teams the user belongs too
async function fetchTeamMembers(options: { testing: boolean } = { testing: false }) {
  const payload: APIRequestPayload = {
    errorCode: ErrorCode.TEAMS,
    method: XHR_REQUEST_TYPE.GET,
    credentials: true,
    route: `/api/teams/members`,
    query: {
      testing: options.testing ? 'true' : 'false'
    }
  }
  const response = await apiService.apiRequest<Record<string, string[]>>(payload)
  if (response.error) {
    response.error.message = 'Unable to get team members'
    dialogStore.actions.handleError(response)
  }
  return response.data
}

interface Actions {
  clearMeetingData: () => void
  selectTeam: (team: string) => void
  setIsTesting: (isTesting: boolean) => void
  getTeamMembers: () => Promise<void>
  getMessages: () => Promise<void>
  getResultsFromOpenAi: (audio: FormData) => Promise<void>
  uploadFile: (file: FormData, path: IPath) => Promise<void>
  getDocumentFeedback: (mode: string, section: string) => Promise<void>
  //getDocumentMetadata: () => Promise<void>
  getVersion: (fileId: string) => Promise<void>
  sortBy: (mode: string) => Promise<void>
}

interface Getters {
  selectedTeam: ComputedRef<string | undefined>
  contributions: ComputedRef<IPieData[]>
  allTeams: ComputedRef<string[]>
  transcribedText: ComputedRef<string | undefined>
  messageAgg: ComputedRef<IMsgAgg | undefined>
  allMessages: ComputedRef<Message | undefined>
  barPostMap: ComputedRef<BarChart[]>
  taskFeedback: ComputedRef<IDocumentFeedback[]>
  meetingFeedback: ComputedRef<IDocumentFeedback[]>
  documentMetaDataAnalytics: ComputedRef<DocumentMetadata>
  version: ComputedRef<IPieData[]>
}

interface ServiceInterface {
  state: State
  actions: Actions
  getters: Getters
}

export function useTeamStore(): ServiceInterface {
  const actions = {
    clearMeetingData: function (): void {
      state.taskFeedback = []
      state.meetingFeedback = []
    },
    /**
     * Get all the Teams the user is part of
     * @returns
     */
    getTeamMembers: async function (): Promise<void> {
      state.teamMembers = {}
      const teamsRes = await fetchTeamMembers({ testing: state.isTesting })
      if (teamsRes) {
        state.teamMembers = teamsRes
      }
    },

    setIsTesting: function (isTesting = false): void {
      state.isTesting = isTesting
    },

    /**
     * Select the team for the analytics to be shown
     * @param teamId
     * @returns
     */
    selectTeam: function (team: string = ''): void {
      if (team) {
        state.selectedTeam = team
        sessionStorage.setItem('team', team)
      }
    },

    /**
     *  fetch all the messages for the selected team
     */
    getMessages: async function (): Promise<void> {
      const response: IMessageResponse = await fetchMessages({ testing: state.isTesting })
      state.allMessages = new Message(response)
      state.messageAgg = state.allMessages.messageAggregate.reduce(
        (acc: IMsgAgg, curr: IMessageAggregate) => {
          acc.mentionsCount = curr.mentionsCount + acc.mentionsCount
          acc.messageCount = curr['body.content'] + acc.messageCount
          acc.reactionsCount = curr.reactionsCount + acc.reactionsCount
          acc.attachmentsCount = curr.attachmentsCount + acc.attachmentsCount

          return acc
        },
        {
          mentionsCount: 0,
          reactionsCount: 0,
          attachmentsCount: 0,
          messageCount: 0
        }
      )
      state.allMessages.messageAggregate.forEach((item) =>
        state.contributions.push(new Contribution(item))
      )
      // Count the messages for each user
      state.barPostMap = state.allMessages.postCount.map((msg) => {
        return {
          date: msg.createdDateTime as unknown as number,
          [msg['from.user.displayName']]: msg.postCount
        }
      })
    },
    getResultsFromOpenAi: async (audio: FormData): Promise<void> => {
      const response: AIResponse = await fetchOpenAi(audio)
      state.transcribedText = response.transcript
    },
    uploadFile: async (file: FormData, path: IPath): Promise<void> => {
      await postUpload(file, path)
      alert('Uploaded')
    },

    getDocumentFeedback: async (mode: string, section: string): Promise<void> => {
      const documentPrompt = CMSGetters.prompts.value.get(
        PROMPTYPE[mode as keyof typeof PROMPTYPE] + section
      )
      console.log('Document Prompt: ', documentPrompt)
      if (documentPrompt) {
        const response = await fetch_document_feedback(documentPrompt as PromptSchema)
        if (mode == PROMPTYPE.Document) {
          response.forEach((item) => {
            // If the response returned already exists inside the state variable for storing ChatGPT feedback info,
            // dont push to add to the variable.
            if (
              !state.taskFeedback.some(
                (tf) => item.filename == tf.filename && item.section == tf.section
              )
            ) {
              state.taskFeedback.push(item)
            }
          })
          console.log('State Document: ', state.taskFeedback)
        } else {
          response.forEach((item) => {
            // If the response returned already exists inside the state variable for storing ChatGPT feedback info,
            // dont push to add to the variable.
            if (
              !state.meetingFeedback.some(
                (mf) => item.filename == mf.filename && item.section == mf.section
              )
            ) {
              state.meetingFeedback.push(item)
            }
          })
          console.log('State Meeting: ', state.meetingFeedback)
        }
        return Promise.resolve()
      }
    },

    /* getDocumentMetadata: async (): Promise<void> => {
      const response = await fetchDocumentMetadata()
      state.documentMetaDataAnalytics = new DocumentMetadata(response)
      console.log(
        '(getDocumentMetadata) state.documentMetaDataAnalytics: ',
        state.documentMetaDataAnalytics
      )
      console.log('(getDocumentMetadata) state.taskFeedback: ', state.taskFeedback)
      state.documentMetaDataAnalytics.versionInfo.map((item) => {
        const gptFeedback = state.taskFeedback.find(
          (feedback) => feedback.filename == item.fileName
        )
        console.log('Item Feedback :', gptFeedback)
        if (gptFeedback) {
          const index = state.taskFeedback.indexOf(gptFeedback)
          if (index > -1) {
            state.taskFeedback.splice(index, 1)
          }
          item.Feedback = gptFeedback?.filefeedback as string
        }
      })
    }, */

    getVersion: async (fileName: string): Promise<void> => {
      state.version = []
      state.documentMetaDataAnalytics.versionInfo.map((item) => {
        if (item.fileName == fileName) {
          for (const [key, value] of Object.entries(item.versionByUser)) {
            console.log(`${key}: ${value}`)
            const obj: IPieData = {
              key: key,
              value: value
            }
            state.version.push(obj)
          }
        }
      })
    },
    sortBy: async (mode: string): Promise<void> => {
      if (mode == SORTTYPE.date) {
        state.documentMetaDataAnalytics.versionInfo.sort((a, b) => {
          const dateA = new Date(a.createdDate.replace(/\./g, '-'))
          const dateB = new Date(b.createdDate.replace(/\./g, '-'))

          return dateB.getTime() - dateA.getTime()
        })
      } else if (mode == SORTTYPE.title) {
        state.documentMetaDataAnalytics.versionInfo.sort((a, b) =>
          b.fileName.localeCompare(a.fileName)
        )
      }
    }
  }

  const getters = {
    get selectedTeam(): ComputedRef<string | undefined> {
      return computed(() => state.selectedTeam) // This is the 'currently selected' Team
    },
    get contributions(): ComputedRef<IPieData[]> {
      return computed(() => state.contributions)
    },
    get allTeams(): ComputedRef<string[]> {
      return computed(() => Object.keys(state.teamMembers)) // Unlikely to change during app usage, but ok as long as the array itself is not overwitten
    },
    get allMessages(): ComputedRef<Message | undefined> {
      return computed(() => state.allMessages) // An array of posts with channel_ids as a key
    },
    get messageAgg(): ComputedRef<IMsgAgg | undefined> {
      return computed(() => state.messageAgg)
    },

    get barPostMap(): ComputedRef<BarChart[]> {
      return computed(() => state.barPostMap)
    },

    get transcribedText(): ComputedRef<string | undefined> {
      return computed(() => state.transcribedText)
    },
    get taskFeedback(): ComputedRef<IDocumentFeedback[]> {
      return computed(() => state.taskFeedback)
    },
    get meetingFeedback(): ComputedRef<IDocumentFeedback[]> {
      return computed(() => state.meetingFeedback)
    },
    get documentMetaDataAnalytics(): ComputedRef<DocumentMetadata> {
      return computed(() => state.documentMetaDataAnalytics)
    },
    get version(): ComputedRef<IPieData[]> {
      return computed(() => state.version)
    }
  }

  return { state, actions, getters }
}

export type useTeamStore = ReturnType<typeof useTeamStore>
